/**
 * Renders a Chinese zodiac character with a PDF-safe CJK font stack.
 *
 * Chromium/Skia PDF often fails to fall back from Latin `font-serif` faces
 * (Georgia / Times) to system CJK fonts, producing grey tofu boxes beside
 * Rabbit / Goat / Monkey labels (Destiny Blueprint audit #3). This component
 * forces the embedded Noto Sans TC zodiac subset (see destinyBlueprintPdfStyles).
 */

import React from "react";

export type ZodiacChineseGlyphProps = {
  /** Single Chinese zodiac character (e.g. "兔"). */
  character: string;
  /** Extra class names for size / color (inherits parent when omitted). */
  className?: string;
  /**
   * When true, marks the glyph for Destiny print CSS (`data-db-pdf-cjk`)
   * so the embedded subset font wins over serif ancestors.
   */
  forPdfCapture?: boolean;
};

/**
 * Inline Chinese zodiac glyph with an explicit CJK-capable font stack.
 */
export const ZodiacChineseGlyph: React.FC<ZodiacChineseGlyphProps> = ({
  character,
  className,
  forPdfCapture,
}) => {
  const trimmed = character.trim();
  if (trimmed.length === 0) {
    return null;
  }

  return (
    <span
      className={className}
      lang="zh-Hant"
      {...(forPdfCapture ? { "data-db-pdf-cjk": "" } : {})}
      style={
        forPdfCapture
          ? undefined
          : {
              fontFamily:
                "\"Noto Sans TC\", \"PingFang TC\", \"PingFang SC\", \"Hiragino Sans GB\", \"Microsoft YaHei\", \"Heiti TC\", sans-serif",
            }
      }
    >
      {trimmed}
    </span>
  );
};

export default ZodiacChineseGlyph;
